import { getPathParamsForCorner, getSVGPathFromPathParams } from './draw';

export interface FigmaSquircleParams {
  cornerRadius: number;
  cornerSmoothing: number;
  width: number;
  height: number;
  preserveSmoothing?: boolean;
}

// Global cache for paths - using LRU-style with size limit
const PATH_CACHE = new Map<string, string>();
const MAX_CACHE_SIZE = 100;

function generateCacheKey(params: FigmaSquircleParams): string {
  const { cornerRadius, cornerSmoothing, width, height, preserveSmoothing = false } = params;
  // Round to avoid floating point precision issues
  const w = Math.round(width * 100) / 100;
  const h = Math.round(height * 100) / 100;
  const r = Math.round(cornerRadius * 100) / 100;
  const s = Math.round(cornerSmoothing * 1000) / 1000;

  return `${w}x${h}r${r}s${s}p${preserveSmoothing}`;
}

function maintainCacheSize() {
  if (PATH_CACHE.size > MAX_CACHE_SIZE) {
    // Remove oldest entries (first 10)
    const keysToDelete = Array.from(PATH_CACHE.keys()).slice(0, 10);
    keysToDelete.forEach((key) => PATH_CACHE.delete(key));
  }
}

export function getSvgPath(params: FigmaSquircleParams): string {
  const cacheKey = generateCacheKey(params);

  // Check cache first
  if (PATH_CACHE.has(cacheKey)) {
    // Move to end (LRU behavior)
    const cachedPath = PATH_CACHE.get(cacheKey)!;
    PATH_CACHE.delete(cacheKey);
    PATH_CACHE.set(cacheKey, cachedPath);
    return cachedPath;
  }

  const { cornerRadius, cornerSmoothing, width, height, preserveSmoothing = false } = params;

  // Calculate rounding budget once
  const roundingAndSmoothingBudget = Math.min(width, height) / 2;
  const effectiveCornerRadius = Math.min(cornerRadius, roundingAndSmoothingBudget);

  // Get path params once for all corners (since they're all the same)
  const pathParams = getPathParamsForCorner({
    cornerRadius: effectiveCornerRadius,
    cornerSmoothing,
    preserveSmoothing,
    roundingAndSmoothingBudget,
  });

  // Generate the SVG path
  const svgPath = getSVGPathFromPathParams({
    width,
    height,
    topLeftPathParams: pathParams,
    topRightPathParams: pathParams,
    bottomLeftPathParams: pathParams,
    bottomRightPathParams: pathParams,
  });

  // Cache the result
  PATH_CACHE.set(cacheKey, svgPath);
  maintainCacheSize();

  return svgPath;
}
